import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  AccountCircle as UserIcon,
  Security as SecurityIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import DocumentTypeTemplate from '../DocumentTypeTemplate';

const UserRoles = ({ projectId, existingContent = '', onSave }) => {
  const renderUserRolesPreview = (content) => {
    // Try to parse as JSON first
    try {
      const rolesData = JSON.parse(content);
      return renderStructuredRoles(rolesData);
    } catch (e) {
      // If not JSON, render as markdown
      return (
        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
          <ReactMarkdown>{content}</ReactMarkdown>
        </Box>
      );
    }
  };
  
  // Render a structured roles visualization
  const renderStructuredRoles = (rolesData) => {
    if (!rolesData.roles || !Array.isArray(rolesData.roles) || rolesData.roles.length === 0) {
      return (
        <Typography variant="body1" color="text.secondary" align="center">
          No user roles defined
        </Typography>
      );
    }
    
    return (
      <Box>
        <Grid container spacing={3}>
          {rolesData.roles.map((role, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {role.name === 'Administrator' || role.name === 'Admin' ? (
                      <AdminIcon color="primary" sx={{ mr: 1 }} />
                    ) : role.name === 'Manager' ? (
                      <ManagerIcon color="primary" sx={{ mr: 1 }} />
                    ) : (
                      <UserIcon color="primary" sx={{ mr: 1 }} />
                    )}
                    <Typography variant="h6">{role.name}</Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {role.description || 'No description provided'}
                  </Typography>
                  
                  {role.permissions && role.permissions.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Permissions
                      </Typography>
                      <List dense>
                        {role.permissions.map((permission, permIndex) => (
                          <ListItem key={permIndex} dense sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={permission.name || permission} 
                              secondary={permission.description} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {rolesData.permissionMatrix && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Permission Matrix
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Permission</TableCell>
                    {rolesData.roles.map(role => (
                      <TableCell key={role.name} align="center">{role.name}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(rolesData.permissionMatrix).map(([permission, roles], index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {permission}
                      </TableCell>
                      {rolesData.roles.map(role => (
                        <TableCell key={role.name} align="center">
                          {roles[role.name] ? (
                            <CheckIcon fontSize="small" color="success" />
                          ) : (
                            <CloseIcon fontSize="small" color="error" />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    );
  };
  
  // User roles prompt configuration
  const userRolesPromptConfig = {
    system: "You are a user experience and security expert specializing in user role definitions, permissions, and access control systems.",
    examples: [
      "Define the user roles for a healthcare management system",
      "Create a permission matrix for our e-commerce platform"
    ],
    instruction: "Design detailed user roles with permissions and access control definitions based on industry best practices."
  };
  
  return (
    <DocumentTypeTemplate
      projectId={projectId}
      documentType="userRoles"
      title="User Roles & Permissions"
      icon={PeopleIcon}
      existingContent={existingContent}
      onSave={onSave}
      renderPreview={renderUserRolesPreview}
      promptConfig={userRolesPromptConfig}
      allowedImportTypes="application/json,text/plain,text/markdown"
    >
      {/* Additional user roles content */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Common User Role Templates</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AdminIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Administrator</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  System administrators with full access to all features and settings.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip label="Full Access" size="small" />
                  <Chip label="User Management" size="small" />
                  <Chip label="System Config" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ManagerIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Manager</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Management users with access to reports and team management.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip label="Reporting" size="small" />
                  <Chip label="Team Management" size="small" />
                  <Chip label="Content Approval" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Standard User</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Regular users with access to core functionality.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip label="Content Creation" size="small" />
                  <Chip label="Profile Management" size="small" />
                  <Chip label="Basic Features" size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Access Control Implementation</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Role-Based Access Control (RBAC)
              </Typography>
              <Typography variant="body2" paragraph>
                RBAC assigns permissions to roles, and roles to users. This approach simplifies administration and scales well.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Define roles based on job functions" 
                    secondary="Roles should reflect organizational structure and responsibilities" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Implement principle of least privilege" 
                    secondary="Users should have minimum permissions needed for their tasks" 
                  />
                </ListItem>
              </List>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Permission Types
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Permission Type</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Read</TableCell>
                      <TableCell>View information only</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Write</TableCell>
                      <TableCell>Create and edit content</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Delete</TableCell>
                      <TableCell>Remove content</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Admin</TableCell>
                      <TableCell>Full control including settings</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </DocumentTypeTemplate>
  );
};

export default UserRoles;